import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import ProtectedRoute from './ProtectedRoute';
import { AppErrorBoundary } from './AppErrorBoundary';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import CreateAccountPage from '@/pages/CreateAccountPage';
import ModuleDashboardPage from '@/pages/dashboard/ModuleDashboardPage';
import { AgentContextProvider } from '@/context/AgentContextProvider';
import NotFoundPage from '@/pages/NotFoundPage';
import LoadingMango from '@/components/common/LoadingMango';
import GridArtBanner from '@/components/common/GridArtBanner';
import { EXPERIMENT_ROUTES } from '@/routes/experiments';
import { REACT_ROUTER_FUTURE_FLAGS } from './futureFlags';

// Lazy-load heavy dashboard pages to keep initial bundle small
const ResultsDashboard = lazy(() =>
  import('@/pages/ResultsDashboard').then((m) => ({ default: m.ResultsDashboard })),
);
const TwinDashboard = lazy(() =>
  import('@/pages/TwinDashboard').then((m) => ({ default: m.TwinDashboard })),
);
// Congestion and Discovery are data-heavy analytics pages — split into separate chunks
const CongestionDashboard = lazy(() =>
  import('@/pages/CongestionDashboard').then((m) => ({ default: m.CongestionDashboard })),
);
const DiscoveryResults = lazy(() =>
  import('@/pages/DiscoveryResults').then((m) => ({ default: m.DiscoveryResults })),
);
const ModulePage = lazy(() => import('@/pages/modules/ModulePage'));
const TuningPage = lazy(() => import('@/pages/modules/TuningPage'));
const ConstraintMapPage = lazy(() => import('@/pages/modules/ConstraintMapPage'));
const TradingLabPage = lazy(() => import('@/pages/modules/TradingLabPage'));

/**
 * SkipNav — WCAG 2.1 SC 2.4.1 skip navigation link.
 * Visually hidden until focused; jumps keyboard focus to the main content landmark.
 */
function SkipNav(): JSX.Element {
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[9999]
                 focus:px-4 focus:py-2 focus:bg-blue-700 focus:text-white focus:rounded focus:shadow-lg
                 focus:text-sm focus:font-medium focus:outline-none focus:ring-2 focus:ring-blue-300"
    >
      Skip to main content
    </a>
  );
}

/**
 * RouteChangeLoader -- debounced navigation progress indicator.
 *
 * Rules:
 *   - If the new page renders in < 200ms: show nothing (no flash).
 *   - If rendering takes >= 200ms: show LoadingMango for a minimum of
 *     500ms so it does not flash on then immediately off.
 *
 * Implementation:
 *   1. On location change, set a 200ms "debounce" timer.
 *   2. If the component is still mounted when that timer fires, enable
 *      the loading indicator and set a 500ms minimum-display timer.
 *   3. When the page finishes (Suspense resolves → component re-renders
 *      naturally), the cleanup effect runs and cancels pending timers;
 *      if the minimum-display timer already started, let it drain.
 */
function RouteChangeLoader(): JSX.Element {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  // Track whether the minimum-display window is in progress so we don't
  // hide the spinner before 500ms have elapsed.
  const minDisplayActive = useRef(false);

  useEffect(() => {
    let debounceTimer: number | null = null;
    let minDisplayTimer: number | null = null;
    let cancelled = false;

    // Step 1 — wait 200ms before showing anything.
    debounceTimer = window.setTimeout(() => {
      if (cancelled) return;
      setIsLoading(true);
      minDisplayActive.current = true;

      // Step 2 — after 500ms minimum display, clear the indicator.
      minDisplayTimer = window.setTimeout(() => {
        if (cancelled) return;
        minDisplayActive.current = false;
        setIsLoading(false);
      }, 500);
    }, 200);

    return () => {
      cancelled = true;
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      // If the minimum-display window hasn't started, safe to hide now.
      if (!minDisplayActive.current) {
        setIsLoading(false);
      }
      // If it has started, the minDisplayTimer will clear it on schedule.
      if (minDisplayTimer !== null) window.clearTimeout(minDisplayTimer);
    };
  }, [location.key]);

  return <LoadingMango isVisible={isLoading} />;
}

export default function AppRouter(): JSX.Element {
  return (
    <AppErrorBoundary>
      <BrowserRouter future={REACT_ROUTER_FUTURE_FLAGS}>
        {/* WCAG 2.1 SC 2.4.1 — skip navigation link */}
        <SkipNav />
        <RouteChangeLoader />
        <GridArtBanner />
        {/* id="main-content" is the target for the skip-nav link (WCAG 2.4.1) */}
        <main id="main-content">
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/create-account" element={<CreateAccountPage />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<ModuleDashboardPage />} />
              <Route
                path="/modules/tuning"
                element={
                  <Suspense fallback={<LoadingMango isVisible />}>
                    <TuningPage />
                  </Suspense>
                }
              />
              <Route path="/modules/model-tuner" element={<Navigate to="/modules/tuning" replace />} />
              <Route
                path="/modules/trading-lab"
                element={
                  <Suspense fallback={<LoadingMango isVisible />}>
                    <TradingLabPage />
                  </Suspense>
                }
              />
              <Route
                path="/modules/constraint-map"
                element={
                  <Suspense fallback={<LoadingMango isVisible />}>
                    <AgentContextProvider moduleId="constraint-map">
                      <ConstraintMapPage />
                    </AgentContextProvider>
                  </Suspense>
                }
              />
              <Route
                path="/modules/:moduleId"
                element={
                  <Suspense fallback={<LoadingMango isVisible />}>
                    <ModulePage />
                  </Suspense>
                }
              />
              {/* Results dashboard — shows solver output for a completed UC job */}
              <Route
                path="/results/:jobId"
                element={
                  <Suspense fallback={<LoadingMango isVisible />}>
                    <ResultsDashboard />
                  </Suspense>
                }
              />
              {/* Digital Grid Twin real-time monitoring dashboard */}
              <Route
                path="/twin"
                element={
                  <Suspense fallback={<LoadingMango isVisible />}>
                    <TwinDashboard />
                  </Suspense>
                }
              />
              {/* Congestion monitoring — real-time alerts + historical binding constraints */}
              <Route
                path="/congestion"
                element={
                  <Suspense fallback={<LoadingMango isVisible />}>
                    <CongestionDashboard />
                  </Suspense>
                }
              />
              {/* AI Scenario Discovery Engine results */}
              <Route
                path="/discovery/:discoveryId"
                element={
                  <Suspense fallback={<LoadingMango isVisible />}>
                    <DiscoveryResults />
                  </Suspense>
                }
              />
              {/* Experiment routes — KR3.3: scenario comparison, sensitivity analysis, Pareto frontier */}
              {EXPERIMENT_ROUTES.map((r) => (
                <Route key={r.path} path={r.path} element={r.element} />
              ))}
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}
